import { Link, usePage } from '@inertiajs/react';
import { BlogLayout } from './BlogLayout';

// Types
interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  visibility: string; // 'visible' or 'auth_only'
  status: string;
  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  } | null;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  postCount: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  nextPage: number | null;
  prevPage: number | null;
}

interface PageProps extends Record<string, unknown> {
  category?: Category;
  posts?: Post[];
  pagination?: Pagination;
  tags?: Array<{ id: number; name: string; slug: string; postCount: number }>;
}

// Helper function to format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Premium Badge Component
function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Premium
    </span>
  );
}

// Post Card Component
function PostCard({ post }: { post: Post }) {
  const isPremium = post.visibility === 'auth_only';
  
  return (
    <article className="bg-card dark:bg-card text-foreground rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow">
      {post.coverImageUrl && (
        <Link href={`/blog/${post.slug}`} className="block">
          <div className="aspect-video relative overflow-hidden">
            <img 
              src={post.coverImageUrl} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
            {isPremium && (
              <div className="absolute top-3 right-3">
                <PremiumBadge />
              </div>
            )}
          </div>
        </Link>
      )}
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          {isPremium && (
            <PremiumBadge />
          )}
          {(post.categories?.length ?? 0) > 0 && (
            post.categories.slice(0, 1).map((category) => (
              <Link
                key={category.id}
                href={`/blog/category/${category.slug}`}
                className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full hover:bg-primary/20 transition-colors"
              >
                {category.name}
              </Link>
            ))
          )}
        </div>
        {/* Tags */}
        {(post.tags?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {post.tags.slice(0, 2).map((tag) => (
              <Link
                key={tag.id}
                href={`/blog/tag/${tag.slug}`}
                className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full hover:bg-muted/80 transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
            {post.tags.length > 2 && (
              <span className="text-xs text-muted-foreground py-1">+{post.tags.length - 2}</span>
            )}
          </div>
        )}
        <Link href={`/blog/${post.slug}`}>
          <h3 className="text-lg font-semibold text-foreground mb-2 hover:text-primary transition-colors">
            {post.title}
          </h3>
        </Link>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {post.summary}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            {post.author && (
              <>
                <span>{post.author.name}</span>
                <span>•</span>
              </>
            )}
            <span>{formatDate(post.publishedAt)}</span>
          </div>
          <span>{post.readingTimeMinutes} min</span>
        </div>
      </div>
    </article>
  );
}

// Pagination Component
function Pagination({ pagination, categorySlug }: { pagination: Pagination; categorySlug: string }) {
  const { currentPage, totalPages, prevPage, nextPage } = pagination;
  
  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center items-center space-x-2 mt-12">
      {prevPage ? (
        <Link
          href={`/blog/category/${categorySlug}/page/${prevPage}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Previous
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Previous
        </span>
      )}
      
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <Link
            key={page}
            href={`/blog/category/${categorySlug}/page/${page}`}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              page === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-muted'
            }`}
          >
            {page}
          </Link>
        ))}
      </div>
      
      {nextPage ? (
        <Link
          href={`/blog/category/${categorySlug}/page/${nextPage}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Next
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Next
        </span>
      )}
    </div>
  );
}

// Main Category Page
export default function BlogCategory() {
  const props = usePage<PageProps>().props;
  const category = props.category;
  const posts = props.posts || [];
  const pagination = props.pagination || { currentPage: 1, totalPages: 1, totalCount: 0, perPage: 12, nextPage: null, prevPage: null };

  // SSR safety check
  if (!category) {
    return (
      <BlogLayout>
        <div className="max-w-4xl mx-auto py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout>
      <div className="space-y-12">
        {/* Category Header */}
        <section className="bg-card dark:bg-card text-foreground rounded-xl border border-border p-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Link href="/blog" className="hover:text-primary">Blog</Link>
            <span>/</span>
            <span className="text-foreground">{category.name}</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{category.name}</h1>
          {category.description && (
            <p className="text-muted-foreground">{category.description}</p>
          )}
          <p className="text-sm text-muted-foreground mt-4">
            {pagination.totalCount} {pagination.totalCount === 1 ? 'post' : 'posts'}
          </p>
        </section>

        {/* Posts Grid */}
        <section>
          {(posts?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-card dark:bg-card text-foreground rounded-lg border border-border">
              <svg className="w-16 h-16 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <h3 className="text-lg font-medium text-foreground mb-2">No posts in this category</h3>
              <p className="text-muted-foreground">Check back soon for new content!</p>
              <Link href="/blog" className="inline-block mt-4 text-primary hover:text-primary/80">
                Browse all posts →
              </Link>
            </div>
          )}
        </section>

        {/* Pagination */}
        <Pagination pagination={pagination} categorySlug={category.slug} />
      </div>
    </BlogLayout>
  );
}
